// ErrorLogView — admin revisa erros capturados no front e no Worker.
// [[project-error-monitoring]]

import { useEffect, useState } from "preact/hooks";
import { AlertTriangle, Bug, Loader2, RefreshCw, Server, Trash2 } from "lucide-react";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { api, type ErrorLogRow } from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";

type Filter = "all" | "client" | "worker";

export function ErrorLogView() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [rows, setRows] = useState<ErrorLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<Filter>("all");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { rows, total } = await api.errorLogList({
        source: filter === "all" ? undefined : filter,
        limit: 200
      });
      setRows(rows);
      setTotal(total);
      setPhase("ready");
    } catch (err) {
      setErrMsg(friendlyError(err));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, [filter]);

  async function clearOld() {
    const ok = await openConfirm({
      title: "Limpar erros antigos?",
      message: "Remove os registros com mais de 30 dias. Não pode ser desfeito.",
      confirmLabel: "Limpar > 30 dias",
      cancelLabel: "Cancelar",
      danger: true
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.errorLogClear(30);
      await load();
      await openAlert({ title: "Pronto", message: "Erros antigos removidos." });
    } catch (err) {
      setErrMsg(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="space-y-5">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <Bug className="size-5 text-violet-500" />
          <div>
            <h1 class="text-lg font-bold">Monitoramento de erros</h1>
            <p class="text-xs text-muted-foreground">
              Quebras capturadas no front (browser) e no Worker (API).
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void load()}
            class="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            <RefreshCw className="size-3.5" /> Atualizar
          </button>
          <button
            type="button"
            onClick={() => void clearOld()}
            disabled={busy}
            class="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
          >
            <Trash2 className="size-3.5" /> Limpar &gt; 30 dias
          </button>
        </div>
      </header>

      <div class="flex flex-wrap gap-1">
        {(
          [
            ["all", "Todos"],
            ["client", "Front (browser)"],
            ["worker", "Worker (API)"]
          ] as const
        ).map(([f, label]) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            class={
              "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors " +
              (filter === f
                ? "border-violet-500 bg-violet-500/15 text-violet-700 dark:text-violet-300"
                : "border-border hover:bg-accent")
            }
          >
            {label}
          </button>
        ))}
        <span class="ml-auto self-center text-xs text-muted-foreground">
          {total} registro(s)
        </span>
      </div>

      {errMsg && (
        <div class="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="size-4" /> {errMsg}
        </div>
      )}

      {phase === "loading" ? (
        <div class="flex items-center gap-2 p-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </div>
      ) : rows.length === 0 ? (
        <p class="rounded-md border border-border bg-card/60 p-6 text-center text-sm text-muted-foreground">
          Nenhum erro registrado. 🎉
        </p>
      ) : (
        <ul class="space-y-2">
          {rows.map((r) => (
            <li
              key={r.id}
              class="rounded-md border border-border bg-card/60 p-3 text-sm"
            >
              <button
                type="button"
                onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                class="flex w-full items-start gap-2 text-left"
              >
                <span
                  class={
                    "mt-0.5 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium " +
                    (r.source === "worker"
                      ? "border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300"
                      : "border-sky-500/40 bg-sky-500/15 text-sky-700 dark:text-sky-300")
                  }
                >
                  {r.source === "worker" ? (
                    <Server className="size-3" />
                  ) : (
                    <Bug className="size-3" />
                  )}
                  {r.source}
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block break-words font-medium">{r.message}</span>
                  <span class="mt-0.5 block text-xs text-muted-foreground">
                    {new Date(r.createdAt).toLocaleString("pt-BR")}
                    {r.path ? ` · ${r.path}` : ""}
                    {r.userEmail ? ` · ${r.userEmail}` : ""}
                  </span>
                </span>
              </button>
              {expanded === r.id && (
                <div class="mt-2 space-y-2 border-t border-border pt-2 text-xs">
                  {r.stack && (
                    <pre class="max-h-64 overflow-auto rounded bg-muted p-2 font-mono text-[11px] leading-relaxed">
                      {r.stack}
                    </pre>
                  )}
                  <div class="grid gap-1 text-muted-foreground sm:grid-cols-2">
                    {r.userAgent && <span>UA: {r.userAgent}</span>}
                    {r.release && <span>Release: {r.release}</span>}
                    {r.meta && (
                      <span class="sm:col-span-2">
                        meta: {JSON.stringify(r.meta)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
